import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDischargeSummary } from "../../../store/slices/dischargeSummarySlice";
import logo from "../../../assets/images/logo-samplee.png";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";

const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
const sigSrc = (v) => (!v ? null : v.startsWith("data:") ? v : `${STORAGE_URL}${v}`);
const isImagePath = (p) => !!p && /\.(jpe?g|png|gif|webp|bmp)$/i.test(p);
const attSrc = (p) => (!p ? null : p.startsWith("data:") ? p : `${STORAGE_URL}${p}`);
const cb = (on) => (on ? "☑" : "☐");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const toArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return String(v).split(",").map((s) => s.trim()).filter(Boolean);
};

const DISCHARGE_TYPE_OPTIONS = [
  "Planned Discharge",
  "Administrative Grounds",
  "Medical Grounds",
  "Transferred to other facility (Referral form provided as per policy)",
  "LAMA Left Against Medical Advise (Counseling done, consequences explained as per LAMA policy)",
  "DOR – Discharge on Request",
];
const SERVICES_OPTIONS = [
  "Psychiatric consultation & treatment", "Primary-care services", "Psychiatric rehabilitation services",
  "Individual Therapy", "Group Therapy", "Psychoeducation for the client", "Psychoeducation for the family",
  "Family Counseling", "Recreational / Occupational Therapy", "Activities of Daily Living (ADL) Support",
  "Nutritional Counseling, Physical Wellness Training", "Weekly and monthly outings",
];
const PROGRESS_OPTIONS = ["Marked Improvement", "Moderate Progress", "No Change", "Needs Further Intervention"];
const CONDITION_OPTIONS = [
  "Stable", "Improved", "Readiness to Discharge", "Risk Managed", "Minimal Supervision Required",
  "Requires Long-Term Monitoring", "Insight Improved but Partial", "Symptoms Persist – Controlled", "Behavioral Support Needed",
];
const IRP_DOMAIN_OPTIONS = ["Living", "Learning", "Socializing", "Working"];
const INSTRUCTIONS_OPTIONS = [
  "Verbal instructions given", "Written prescription attached", "Continue till next psychiatric review", "Side effects & compliance discussed",
];
const FAMILY_COUNSELING_OPTIONS = [
  "Client psychiatric illness", "Maintaining hygiene",
  "Medication use side effects and monitoring /supervision for compliance including checking mouth after taking medication",
  "Follow-up after 14 days / earlier if symptoms worsen", "Addressing family expectations", "Monitor closely for any increase in symptoms",
];
const EDUCATION_OPTIONS = ["Client", "Family", "Guardian"];
// value -> display label (Day Care stored value shows as Day Rehab)
const TRANSITION_OPTIONS = [
  { label: "Day Rehab", value: "Day Care" },
  { label: "Home Visitation", value: "Home Visitation" },
  { label: "Vocational Activities", value: "Vocational Activities" },
  { label: "Other", value: "Other" },
];

export default function DischargeSummaryFormPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentDischargeSummary, isLoading, error } = useSelector((state) => state.dischargeSummary || {});

  useEffect(() => {
    if (id) dispatch(fetchDischargeSummary(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDischargeSummary) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => { clearTimeout(timer); window.onafterprint = null; };
    }
  }, [currentDischargeSummary, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentDischargeSummary) return <div className="p-4 text-center">No record found.</div>;

  const d = currentDischargeSummary;
  const has = (field, value) => {
    const raw = d[field];
    if (!raw) return false;
    if (Array.isArray(raw)) return raw.includes(value);
    const str = String(raw);
    return str.includes(value) || toArr(str).includes(value);
  };
  const meds = d.medications || [];

  const ChkList = ({ field, options }) => (
    <div className="chk-list">
      {options.map((opt) => (
        <div className="chk" key={opt}>{cb(has(field, opt))} {opt}</div>
      ))}
    </div>
  );

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 12mm 12mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, table { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table { border-collapse: collapse !important; }
          table, th, td { border: 1px solid #000 !important; }
          th { background: #f0f0f0 !important; }
          .section-title { border: 1px solid #000 !important; background: #e9e9e9 !important; }
          .notes-box { border: 1px solid #000 !important; }
          .lbl { background: #f7f7f7 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .ds-print, .ds-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .ds-print { width: 100%; max-width: 820px; margin: 0 auto; padding: 16px; font-size: 10pt; line-height: 1.35; }
        .header-logo { text-align: center; margin-bottom: 4px; }
        .header-logo img { height: 58px; }
        .doc-title { text-align: center; font-size: 15pt; font-weight: bold; text-transform: uppercase; margin: 0; }
        .doc-sub { text-align: center; font-size: 11pt; font-weight: bold; margin: 2px 0; }
        .confidential { border: 1px solid #000; background: #fafafa; padding: 5px 8px; font-size: 8pt; font-style: italic; margin: 6px 0 12px; }
        .section { margin-bottom: 12px; }
        .section-title { font-size: 10.5pt; font-weight: bold; background: #e9e9e9; border: 1px solid #000; padding: 3px 8px; text-transform: uppercase; margin-bottom: 6px; }
        .sub-title { font-weight: bold; font-size: 9.5pt; text-decoration: underline; margin: 6px 0 3px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
        td, th { border: 1px solid #000; padding: 4px 6px; font-size: 9pt; vertical-align: top; }
        th { background: #f0f0f0; text-align: left; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 22%; }
        .chk-list { display: grid; grid-template-columns: 1fr 1fr; gap: 1px 14px; }
        .chk-list.one { grid-template-columns: 1fr; }
        .chk { font-size: 9pt; padding: 1px 0; }
        .notes-box { border: 1px solid #000; min-height: 34px; padding: 4px 6px; white-space: pre-wrap; font-size: 9pt; }
        .footer { text-align: center; font-size: 8.5pt; margin-top: 10px; border-top: 1px solid #000; padding-top: 4px; }
      `}</style>

      <PrintWrapper>
        <div className="ds-print">
          <div className="header-logo"><img src={logo} alt="The Recovery House" /></div>
          <h1 className="doc-title">The Recovery House</h1>
          <div className="doc-sub">Discharge Summary &amp; After Care Plan</div>
          <div className="confidential">
            Confidential Medical Record – This document contains sensitive health information. It is strictly confidential and
            intended only for authorized individuals including the client, their legal guardian, and clinical staff. Unauthorized
            access, sharing, or duplication is prohibited. Handle and store according to TRH confidentiality policy.
          </div>

          {/* Client Information */}
          <div className="section">
            <div className="section-title">Client Information</div>
            <table>
              <tbody>
                <tr><td className="lbl">Name</td><td>{val(d.client_name)}</td><td className="lbl">MR No. / CNIC</td><td>{val(d.CNIC)}</td></tr>
                <tr><td className="lbl">Date of Birth</td><td>{fmt(d.date_of_birth)}</td><td className="lbl">Gender</td><td>{val(d.gender)}</td></tr>
                <tr><td className="lbl">Program</td><td>{val(d.program)}</td><td className="lbl">Date of Admission</td><td>{fmt(d.admission_date)}</td></tr>
                <tr><td className="lbl">Date of Discharge</td><td colSpan={3}>{fmt(d.discharge_date)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Provisional Psychiatric Diagnosis */}
          <div className="section">
            <div className="section-title">Provisional Psychiatric Diagnosis</div>
            <table>
              <tbody>
                <tr><td className="lbl">Primary Diagnosis</td><td>{val(d.primary_diagnosis)}</td></tr>
                <tr><td className="lbl">Secondary Diagnosis</td><td>{val(d.secondary_diagnosis)}</td></tr>
                <tr><td className="lbl">Co-morbidities</td><td>{val(d.co_morbidities)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Discharge Planning */}
          <div className="section">
            <div className="section-title">Discharge Planning</div>
            <div className="sub-title">Discharge Type</div>
            <ChkList field="discharge_types" options={DISCHARGE_TYPE_OPTIONS} />
            <table style={{ marginTop: 6 }}>
              <thead>
                <tr><th style={{ width: "50%" }}>Services Provided at TRH</th><th>Presenting Symptoms</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="chk-list one">
                      {SERVICES_OPTIONS.map((opt) => (<div className="chk" key={opt}>{cb(has("service_provided", opt))} {opt}</div>))}
                    </div>
                  </td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{val(d.presenting_symptoms)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Clinical Reports */}
          <div className="section">
            <div className="section-title">Clinical Reports</div>
            <table>
              <tbody>
                {[
                  ["Psychiatrist's Report", d.psychiatrist_remarks, d.psychiatrist_attachment],
                  ["Case Manager's Report", d.care_manager_remarks, d.care_manager_attachment],
                  ["Physicians Report", d.primary_care_remarks, d.primary_care_attachment],
                ].map(([label, remarks, att]) => (
                  <tr key={label}>
                    <td className="lbl">{label}</td>
                    <td>
                      {val(remarks)}
                      {isImagePath(att) && (
                        <div style={{ marginTop: 4 }}>
                          <img src={attSrc(att)} alt={`${label} attachment`} style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain", border: "1px solid #000" }} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Progress since admission */}
          <div className="section">
            <div className="section-title">Progress since time of admission</div>
            <div className="sub-title">Progress</div>
            <ChkList field="clinical_progress" options={PROGRESS_OPTIONS} />
            <div className="sub-title">Condition at time of discharge</div>
            <ChkList field="mental_state_at_discharge" options={CONDITION_OPTIONS} />
            <div className="sub-title">Complications (if any)</div>
            <div className="notes-box">{d.complications || ""}</div>
          </div>

          {/* IRP, Progress and Outcomes */}
          <div className="section">
            <div className="section-title">Individualized Rehabilitation Plan, Progress and Outcomes</div>
            <div className="chk-list" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              {IRP_DOMAIN_OPTIONS.map((opt) => (<div className="chk" key={opt}>{cb(has("irp_domains", opt))} {opt}</div>))}
            </div>
            <table style={{ marginTop: 6 }}>
              <tbody>
                <tr><td className="lbl">Short-Term Goals</td><td>{val(d.short_term_goals)}</td></tr>
                <tr><td className="lbl">Long-Term Goals</td><td>{val(d.long_term_goals)}</td></tr>
                <tr><td className="lbl">{cb(has("current_progress", "Achieved"))} Achieved</td><td>{val(d.progress_achieved_text)}</td></tr>
                <tr><td className="lbl">{cb(has("current_progress", "In Progress"))} In Progress</td><td>{val(d.progress_in_progress_text)}</td></tr>
                <tr><td className="lbl">{cb(has("current_progress", "Not Achieved"))} Not Achieved</td><td>{val(d.progress_not_achieved_text)}</td></tr>
                <tr><td className="lbl">Additional Responsibilities / current progress</td><td>{val(d.additional_responsibilities)}</td></tr>
                <tr><td className="lbl">Outcomes</td><td>{val(d.outcomes)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Discharge Medication */}
          <div className="section">
            <div className="section-title">Discharge Medication</div>
            <table>
              <thead>
                <tr><th style={{ width: "6%" }}>#</th><th>Medication Name</th><th style={{ width: "24%" }}>Dosage Frequency</th><th style={{ width: "26%" }}>Route</th></tr>
              </thead>
              <tbody>
                {meds.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center" }}>—</td></tr>}
                {meds.map((m, i) => (
                  <tr key={m.id || i}>
                    <td>{String(i + 1).padStart(2, "0")}</td>
                    <td>{val(m.name)}</td>
                    <td>{val(m.dosage)}</td>
                    <td>{cb(m.route === "Oral")} Oral&nbsp; {cb(m.route === "IM")} IM&nbsp; {cb(m.route === "IV")} IV</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Instructions */}
          <div className="section">
            <div className="section-title">Instructions</div>
            <ChkList field="instructions" options={INSTRUCTIONS_OPTIONS} />
          </div>

          {/* Family Counseling */}
          <div className="section">
            <div className="section-title">Family Counseling Done for</div>
            <div className="chk-list one">
              {FAMILY_COUNSELING_OPTIONS.map((opt) => (<div className="chk" key={opt}>{cb(has("family_counseling", opt))} {opt}</div>))}
            </div>
          </div>

          {/* Home Care & Future Support plan */}
          <div className="section">
            <div className="section-title">Home Care &amp; Future Support plan</div>
            <div className="sub-title">Education Provided To</div>
            <div className="chk-list" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
              {EDUCATION_OPTIONS.map((opt) => (<div className="chk" key={opt}>{cb(has("teaching_provided_to", opt))} {opt}</div>))}
            </div>
            <table style={{ marginTop: 6 }}>
              <tbody>
                <tr><td className="lbl">Recovery Goals</td><td>{val(d.recovery_goals)}</td><td className="lbl">Possible Relapse Signs</td><td>{val(d.possible_relapse_signs)}</td></tr>
                <tr><td className="lbl">Injections if Any</td><td>{val(d.injections)}</td><td className="lbl">Risk Assessment at Discharge</td><td>{val(d.risk_assessment)}</td></tr>
                <tr><td className="lbl">Who to Contact</td><td>{val(d.who_to_contact)}</td><td className="lbl">Where to Go in Crisis</td><td>{val(d.where_to_go_crisis)}</td></tr>
                <tr><td className="lbl">Follow-up Psychiatrist Appointment</td><td>{fmt(d.follow_up_psychiatrist)}</td><td className="lbl">Referred For Physical Health Needs — Other</td><td>{val(d.physical_health_needs)}</td></tr>
                <tr><td className="lbl">Special Instructions</td><td colSpan={3}>{val(d.special_instructions)}</td></tr>
              </tbody>
            </table>
            <div className="sub-title">Transition For</div>
            <div className="chk-list" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              {TRANSITION_OPTIONS.map((opt) => (<div className="chk" key={opt.value}>{cb(has("referred_for", opt.value))} {opt.label}</div>))}
            </div>
            {has("referred_for", "Other") && d.referred_for_other_text ? (
              <div style={{ fontSize: "9pt", marginTop: 2 }}>Other: {d.referred_for_other_text}</div>
            ) : null}
            <table style={{ marginTop: 6 }}>
              <tbody>
                <tr>
                  <td className="lbl">Next of Kin Informed</td>
                  <td>{cb(d.next_of_kin_informed === "Yes")} Yes&nbsp; {cb(d.next_of_kin_informed === "No")} No</td>
                  <td className="lbl">Emergency Contact Provided</td>
                  <td>{cb(d.emergency_contact_provided === "Yes")} Yes&nbsp; {cb(d.emergency_contact_provided === "No")} No</td>
                </tr>
                <tr><td className="lbl">Safety Care Plan (if needed)</td><td colSpan={3}>{val(d.safety_care_plan)}</td></tr>
                <tr><td className="lbl">Recommendations from TRH</td><td colSpan={3}>{val(d.trh_recommendations)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Signatures */}
          <div className="section">
            <div className="section-title">Signatures</div>
            <table>
              <thead>
                <tr><th style={{ width: "42%" }}>Role</th><th>Name &amp; Signature</th></tr>
              </thead>
              <tbody>
                {[
                  ["Consultant Psychiatrist", d.consultant_psychiatrist_name, d.consultant_psychiatrist_signature],
                  ["Psychiatric Rehabilitation Practitioner PRP", d.prp_name, d.prp_signature],
                  ["Guardian", d.guardian_name, d.guardian_signature],
                  ["Client", d.client_sign_name, d.client_signature],
                ].map(([role, name, sig]) => (
                  <tr key={role} style={{ height: 46 }}>
                    <td>{role}</td>
                    <td>
                      {name ? <div style={{ fontSize: "9pt", marginBottom: 2 }}>{name}</div> : null}
                      {sigSrc(sig) ? <img src={sigSrc(sig)} alt={`${role} signature`} style={{ height: 40, maxWidth: 220, objectFit: "contain" }} /> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="footer">CONFIDENTIAL — For Authorised Clinical Use Only</div>
        </div>
      </PrintWrapper>
    </>
  );
}
